import { getIn } from 'formik';

import personFieldValueSyncRules from 'constants/personFieldValueSyncRules';

const normalizeValue = (value) => String(value ?? '');

const getFieldName = (field, namePrefix = '') => {
  const name = field?.name ?? field?.fieldname;

  if (typeof name !== 'string') return '';

  return namePrefix && name.startsWith(namePrefix)
    ? name.slice(namePrefix.length)
    : name;
};

const getFullFieldName = (namePrefix, fieldName) =>
  namePrefix ? `${namePrefix}${fieldName}` : fieldName;

const getSelectOptions = (field) => {
  const options = field?.settings?.options ?? field?.options;

  return Array.isArray(options) ? options : [];
};

const getOptionValue = (option) => option?.id ?? option?.value;

const isEmptySelectValue = (value) =>
  value === null
  || typeof value === 'undefined'
  || value === ''
  || (Array.isArray(value) && value.length === 0);

export const arePersonFieldValuesEqual = (left, right) => {
  if (Object.is(left, right)) return true;

  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length
      && left.every((value, index) => arePersonFieldValuesEqual(value, right[index]));
  }

  return false;
};

export const getPersonFieldValueSyncRules = ({
  company,
  roleTemplateId,
  template,
  rules = personFieldValueSyncRules,
} = {}) => {
  if (!Array.isArray(rules)) return [];

  return rules.filter((rule) => {
    const rolesToApply = Array.isArray(rule?.rolToApply)
      ? rule.rolToApply
      : [];
    const roleMatches = rolesToApply.length === 0
      || rolesToApply.some((role) =>
        normalizeValue(role) === normalizeValue(roleTemplateId),
      );

    return normalizeValue(rule?.company) === normalizeValue(company?.id ?? company)
      && normalizeValue(rule?.template) === normalizeValue(template?.id ?? template)
      && roleMatches
      && typeof rule?.sourceField === 'string'
      && rule.sourceField.length > 0
      && typeof rule?.targetField === 'string'
      && rule.targetField.length > 0;
  });
};

export const findPersonFormField = ({
  fields,
  fieldName,
  namePrefix = '',
} = {}) => {
  if (!Array.isArray(fields) || !fieldName) return null;

  return fields.find((field) =>
    getFieldName(field, namePrefix) === fieldName,
  ) ?? null;
};

export const isPersonSelectField = (field) =>
  field?.type === 'select' || field?.displaytype === 'select';

export const doesSelectContainValue = (field, value) => {
  if (isEmptySelectValue(value)) return true;

  const options = getSelectOptions(field);
  const selectedValues = Array.isArray(value) ? value : [value];

  return selectedValues.every((selectedValue) =>
    options.some((option) =>
      normalizeValue(getOptionValue(option)) === normalizeValue(selectedValue),
    ),
  );
};

/**
 * Resuelve la actualización de una regla de sincronización sin modificar
 * Formik. Un resultado nulo significa que la regla no aplica al formulario
 * visible, que el destino no acepta la opción o que ya tiene el mismo valor.
 */
export const getPersonFieldValueSyncUpdate = ({
  fields,
  namePrefix = '',
  rule,
  values,
} = {}) => {
  if (!rule?.sourceField || !rule?.targetField) return null;

  const sourceField = findPersonFormField({
    fields,
    fieldName: rule.sourceField,
    namePrefix,
  });
  const targetField = findPersonFormField({
    fields,
    fieldName: rule.targetField,
    namePrefix,
  });

  if (!sourceField || !targetField) return null;

  const sourceName = getFullFieldName(namePrefix, rule.sourceField);
  const targetName = getFullFieldName(namePrefix, rule.targetField);
  const sourceValue = getIn(values, sourceName);

  if (
    isPersonSelectField(targetField)
    && !doesSelectContainValue(targetField, sourceValue)
  ) {
    return null;
  }

  if (arePersonFieldValuesEqual(getIn(values, targetName), sourceValue)) {
    return null;
  }

  return {
    name: targetName,
    value: sourceValue,
  };
};
